import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { eventBus } from '../eventBus';
import { validate } from '../contracts/validate';
import '../contracts/MensagensProgramadas_SendMessagesInput.ts';
import '../contracts/MensagensProgramadas_GetTemplatesByFranchisesInput.ts';
import '../contracts/MensagensProgramadas_SendMessagesResponse.ts';
import '../contracts/MensagensProgramadas_GetTemplatesByFranchisesResponse.ts';
import '../contracts/MensagensProgramadas_CarregaScheduledMessagesResponse.ts';
import '../contracts/MensagensProgramadas_BuscaWhatsappDevicesResponse.ts';
import '../contracts/MensagensProgramadas_DeleteScheduledMessageResponse.ts';
import '../contracts/MensagensProgramadas_EditScheduledMessageResponse.ts';
import '../contracts/MensagensProgramadas_DeleteScheduledMessageInput';
import '../contracts/MensagensProgramadas_EditScheduledMessageInput';
import '../contracts/MensagensProgramadas_CarregaScheduledMessagesInput';
import '../contracts/MensagensProgramadas_BuscaWhatsappDevicesInput';
import '../contracts/MensagensProgramadas_BuscaWhatsappDevicesResponse';
import '../contracts/MensagensProgramadas_CarregaScheduledMessagesResponse';
import '../contracts/MensagensProgramadas_DeleteScheduledMessageResponse';
import '../contracts/MensagensProgramadas_EditScheduledMessageResponse';
import '../contracts/MensagensProgramadas_GetTemplatesByFranchisesResponse';
import '../contracts/MensagensProgramadas_SendMessagesResponse';

import type { MensagensProgramadas_GetTemplatesByFranchisesInput } from '../contracts/MensagensProgramadas_GetTemplatesByFranchisesInput';
import type { MensagensProgramadas_SendMessagesInput } from '../contracts/MensagensProgramadas_SendMessagesInput';
/**
 * Composable for MensagensProgramadas (Scheduled Messages) operations
 * Provides methods to interact with the Scheduled Messages API
 */
export function useMensagensProgramadas() {
  const isLoading = ref(false);
  const error = ref(null);
  let pollingInterval: any = null;

  /**
   * Fetch scheduled messages based on current filters
   * Uses filtro_model for date range and franchise filtering
   */
  const fetchScheduledMessages = async () => {
    log('useMensagensProgramadas', 'fetchScheduledMessages', 'Fetching scheduled messages');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/mensagens_programadas/carrega_scheduled_messages', {}, {
        inputContract: 'MensagensProgramadas_CarregaScheduledMessagesInput',
        responseContract: 'MensagensProgramadas_CarregaScheduledMessagesResponse'
      });
      log('useMensagensProgramadas', 'fetchScheduledMessages', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao carregar mensagens agendadas');
      }
    } catch (err) {
      log('useMensagensProgramadas', 'fetchScheduledMessages', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch available WhatsApp devices
   */
  const fetchWhatsAppDevices = async () => {
    log('useMensagensProgramadas', 'fetchWhatsAppDevices', 'Fetching WhatsApp devices');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/mensagens_programadas/buscaWhatsappDevices', {}, {
        inputContract: 'MensagensProgramadas_BuscaWhatsappDevicesInput',
        responseContract: 'MensagensProgramadas_BuscaWhatsappDevicesResponse'
      });
      log('useMensagensProgramadas', 'fetchWhatsAppDevices', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar dispositivos WhatsApp');
      }
    } catch (err) {
      log('useMensagensProgramadas', 'fetchWhatsAppDevices', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Send/Schedule messages
   * @param {Array} messages - Array of message objects with cliente_id, template_id, whatsappfranquia_id
   * @param {String} currentDatetime - Current datetime in ISO 8601 format
   */
  const sendMessages = async (messages: any, currentDatetime: any) => {
    log('useMensagensProgramadas', 'sendMessages', 'Sending messages', { messages, currentDatetime });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: MensagensProgramadas_SendMessagesInput = {
        messages,
        current_datetime: currentDatetime
      };
      validate('MensagensProgramadas_SendMessagesInput', payload);
      const response = await httpService.post('/index.php/mensagens_programadas/send_messages', payload, {
        responseContract: 'MensagensProgramadas_SendMessagesResponse'
      });
      log('useMensagensProgramadas', 'sendMessages', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao agendar mensagens');
      }
    } catch (err) {
      log('useMensagensProgramadas', 'sendMessages', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get message templates by franchise IDs
   * @param {Array} franquiaIds - Array of franchise IDs
   */
  const getTemplatesByFranchises = async (franquiaIds: any) => {
    log('useMensagensProgramadas', 'getTemplatesByFranchises', 'Fetching templates', franquiaIds);
    isLoading.value = true;
    error.value = null;

    try {
      const payload: MensagensProgramadas_GetTemplatesByFranchisesInput = { franquia_ids: franquiaIds };
      validate('MensagensProgramadas_GetTemplatesByFranchisesInput', payload);
      const response = await httpService.post('/index.php/mensagens_programadas/get_templates_by_franchises', payload, {
        responseContract: 'MensagensProgramadas_GetTemplatesByFranchisesResponse'
      });
      log('useMensagensProgramadas', 'getTemplatesByFranchises', 'Response', response);

      if (response.success) {
        return response.dados?.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar templates');
      }
    } catch (err) {
      log('useMensagensProgramadas', 'getTemplatesByFranchises', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Cancel a scheduled message
   * @param {String} scheduleId - The ID of the scheduled message to cancel
   */
  const cancelScheduledMessage = async (scheduleId: number) => {
    log('useMensagensProgramadas', 'cancelScheduledMessage', 'Cancelling scheduled message', scheduleId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/mensagens_programadas/deleteScheduledMessage', {
        mensagem_programada_id: scheduleId
      }, {
        responseContract: 'MensagensProgramadas_DeleteScheduledMessageResponse',
        inputContract: 'MensagensProgramadas_DeleteScheduledMessageInput'
      });
      log('useMensagensProgramadas', 'cancelScheduledMessage', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao cancelar mensagem agendada');
      }
    } catch (err) {
      log('useMensagensProgramadas', 'cancelScheduledMessage', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Edit a scheduled message
   * @param {String} scheduleId - The ID of the scheduled message to edit
   * @param {Object} data - The updated message data
   */
  const editScheduledMessage = async (scheduleId: any, data: any) => {
    log('useMensagensProgramadas', 'editScheduledMessage', 'Editing scheduled message', { scheduleId, data });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/mensagens_programadas/editScheduledMessage', {
        mensagem_programada_id: scheduleId,
        scheduled_at: data.scheduled_at
      }, {
        responseContract: 'MensagensProgramadas_EditScheduledMessageResponse',
        inputContract: 'MensagensProgramadas_EditScheduledMessageInput'
      });
      log('useMensagensProgramadas', 'editScheduledMessage', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao editar mensagem agendada');
      }
    } catch (err) {
      log('useMensagensProgramadas', 'editScheduledMessage', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Start polling for updates when there are pending messages
   * Emits 'mensagens-programadas.refresh' every 10 seconds
   */
  const startPolling = () => {
    if (pollingInterval) {
      return;
    }
    log('useMensagensProgramadas', 'startPolling', 'Starting polling every 10 seconds');
    pollingInterval = setInterval(() => {
      log('useMensagensProgramadas', 'startPolling', 'Polling tick - emitting refresh');
      eventBus.emit('mensagens-programadas.refresh');
    }, 10000);
  };

  /**
   * Stop polling for updates
   */
  const stopPolling = () => {
    if (pollingInterval) {
      log('useMensagensProgramadas', 'stopPolling', 'Stopping polling');
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  };

  return {
    isLoading,
    error,
    fetchScheduledMessages,
    fetchWhatsAppDevices,
    sendMessages,
    getTemplatesByFranchises,
    cancelScheduledMessage,
    editScheduledMessage,
    startPolling,
    stopPolling
  };
}
